/*
 * Makefield Bulk Order Customizer
 *
 * Customer-facing catalog + logo customizer for makefield.shop/pages/bulk-orders.
 * - Curated Makefield catalog + Makefield pricing only (Admin-brokered).
 * - Customer adds a logo once; logo-applied mockups are generated for cards
 *   and the design modal via Admin-brokered short-lived sessions.
 * - "Design this item" opens an accessible modal (no page scroll).
 * - Submits to the Admin public intake endpoint as a manual-review request.
 *
 * Internal note: no production partner branding is shown to the customer.
 */
(function () {
  "use strict";

  var DEFAULTS = {
    request: "https://admin.makefield.shop/api/bulk-order-requests",
    catalog: "https://admin.makefield.shop/api/bulk-order-catalog",
    ewlSession: "https://admin.makefield.shop/api/bulk-order-ewl/session",
    embedSession: "https://admin.makefield.shop/api/bulk-order-embed/session",
    logoAssets: "https://admin.makefield.shop/api/bulk-order-logo-assets",
    storeSearch: "https://admin.makefield.shop/api/bulk-order-stores"
  };

  var SDK_TIMEOUT_MS = 12000;
  var STUDIO_TIMEOUT_MS = 18000;
  var MOCKUP_CHUNK = 5;
  var MAX_PREVIEWS = 8;
  var MAX_PREVIEW_DATAURL = 340000;
  var EXTRA_PRINT_UNIT_CENTS = 700;

  // ---- generic helpers -----------------------------------------------------
  function $(root, sel) { return root.querySelector(sel); }
  function $all(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
  function escapeAttr(value) { return escapeHtml(value).replace(/`/g, "&#96;"); }

  function num(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function money(cents) {
    var c = num(cents, 0);
    if (!c) return "$0";
    var dollars = c / 100;
    return "$" + dollars.toLocaleString("en-US", { maximumFractionDigits: c % 100 === 0 ? 0 : 2 });
  }

  function buildUrl(base, params) {
    var url = new URL(base, window.location.origin);
    Object.keys(params || {}).forEach(function (key) {
      var v = params[key];
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(key, v);
    });
    return url.toString();
  }

  function getJson(url) {
    return fetch(url).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok || body.ok === false) throw new Error(body.error || body.message || "Request failed.");
        return body;
      });
    });
  }

  function postJson(url, payload) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {})
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok || body.ok === false) throw new Error(body.error || body.message || "Request failed.");
        return body;
      });
    });
  }

  function isLightHex(hex) {
    if (!hex || !/^#?[0-9a-f]{6}$/i.test(hex.replace("#", "").length === 3 ? "" : hex)) {
      // accept 3 or 6 digit; fall through to a simple parse below
    }
    var h = String(hex || "").replace("#", "");
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (!/^[0-9a-f]{6}$/i.test(h)) return false;
    var r = parseInt(h.slice(0, 2), 16);
    var g = parseInt(h.slice(2, 4), 16);
    var b = parseInt(h.slice(4, 6), 16);
    var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.82;
  }

  function quoteTierText(total) {
    if (total >= 100) return "100+ items \u2014 our best bulk tier. We'll price this carefully.";
    if (total >= 50) return "50\u201399 items \u2014 great for full teams, camps, and tournaments.";
    if (total >= 25) return "25\u201349 items \u2014 better unit pricing and more options.";
    if (total >= 10) return "10\u201324 items \u2014 group pricing starts here.";
    if (total > 0) return "Under 10 items \u2014 you can still send this for review.";
    return "Add a product to start your request.";
  }

  // ---- per-section init ----------------------------------------------------
  function initRoot(root) {
    var endpoints = {
      request: root.getAttribute("data-api-url") || DEFAULTS.request,
      catalog: root.getAttribute("data-catalog-api-url") || DEFAULTS.catalog,
      ewlSession: root.getAttribute("data-ewl-session-api-url") || DEFAULTS.ewlSession,
      embedSession: root.getAttribute("data-embed-session-api-url") || DEFAULTS.embedSession,
      logoAssets: root.getAttribute("data-logo-assets-api-url") || DEFAULTS.logoAssets,
      storeSearch: root.getAttribute("data-store-search-api-url") || DEFAULTS.storeSearch
    };
    var els = {
      notice: $(root, "[data-mfc-notice]"),
      search: $(root, "[data-mfc-search]"),
      resultCount: $(root, "[data-mfc-result-count]"),
      filters: $(root, "[data-mfc-filters]"),
      catalogStatus: $(root, "[data-mfc-catalog-status]"),
      grid: $(root, "[data-mfc-grid]"),
      // logo
      logoDrop: $(root, "[data-mfc-logo-drop]"),
      logoButton: $(root, "[data-mfc-logo-button]"),
      logoInput: $(root, "[data-mfc-logo-input]"),
      logoPreview: $(root, "[data-mfc-logo-preview]"),
      logoThumb: $(root, "[data-mfc-logo-thumb]"),
      logoName: $(root, "[data-mfc-logo-name]"),
      logoClear: $(root, "[data-mfc-logo-clear]"),
      logoStatus: $(root, "[data-mfc-logo-status]"),
      // store picker
      store: $(root, "[data-mfc-store]"),
      storeSearch: $(root, "[data-mfc-store-search]"),
      storeSearchButton: $(root, "[data-mfc-store-search-button]"),
      storeStatus: $(root, "[data-mfc-store-status]"),
      storeResults: $(root, "[data-mfc-store-results]"),
      storeGraphics: $(root, "[data-mfc-store-graphics]"),
      // summary
      totalItems: $(root, "[data-mfc-total-items]"),
      subtotal: $(root, "[data-mfc-subtotal]"),
      tier: $(root, "[data-mfc-tier]"),
      designs: $(root, "[data-mfc-designs]"),
      summaryCta: $(root, "[data-mfc-summary-cta]"),
      // modal
      modal: $(root, "[data-mfc-modal]"),
      modalBackdrop: $(root, "[data-mfc-modal-backdrop]"),
      modalClose: $(root, "[data-mfc-modal-close]"),
      studioBar: $(root, "[data-mfc-studio-bar]"),
      studio: $(root, "[data-mfc-studio]"),
      studioLoading: $(root, "[data-mfc-studio-loading]"),
      studioLoadingText: $(root, "[data-mfc-studio-loading-text]"),
      stage: $(root, "[data-mfc-stage]"),
      stageImg: $(root, "[data-mfc-stage-img]"),
      views: $(root, "[data-mfc-views]"),
      previewNote: $(root, "[data-mfc-preview-note]"),
      modalCat: $(root, "[data-mfc-modal-cat]"),
      modalTitle: $(root, "[data-mfc-modal-title]"),
      modalPrice: $(root, "[data-mfc-modal-price]"),
      extraPrintNote: $(root, "[data-mfc-extra-print-note]"),
      colorsWrap: $(root, "[data-mfc-colors-wrap]"),
      colorName: $(root, "[data-mfc-color-name]"),
      swatches: $(root, "[data-mfc-swatches]"),
      placementWrap: $(root, "[data-mfc-placement-wrap]"),
      placements: $(root, "[data-mfc-placements]"),
      sizesWrap: $(root, "[data-mfc-sizes-wrap]"),
      sizes: $(root, "[data-mfc-sizes]"),
      modalError: $(root, "[data-mfc-modal-error]"),
      modalQty: $(root, "[data-mfc-modal-qty]"),
      modalLine: $(root, "[data-mfc-modal-line]"),
      modalAdd: $(root, "[data-mfc-modal-add]"),
      // form
      form: $(root, "[data-mfc-form]"),
      honeypot: $(root, "[data-mfc-honeypot]"),
      name: $(root, "[data-mfc-name]"),
      email: $(root, "[data-mfc-email]"),
      org: $(root, "[data-mfc-org]"),
      deadline: $(root, "[data-mfc-deadline]"),
      phone: $(root, "[data-mfc-phone]"),
      event: $(root, "[data-mfc-event]"),
      storeUrl: $(root, "[data-mfc-store-url]"),
      role: $(root, "[data-mfc-role]"),
      customNote: $(root, "[data-mfc-custom-note]"),
      notes: $(root, "[data-mfc-notes]"),
      submit: $(root, "[data-mfc-submit]"),
      error: $(root, "[data-mfc-error]"),
      confirm: $(root, "[data-mfc-confirm]")
    };

    var state = {
      products: [],
      byHandle: {},
      hydrated: false,
      activeCategory: "all",
      search: "",
      categories: [],
      clientSessionId: "mfc-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
      logo: null,            // { source, file, fileName, contentType, publicImageUrl, assetId, sha256, publicUrlPromise }
      storeGraphic: null,
      storeResults: [],
      storeGraphicsList: [],
      sdk: null,
      sdkPromise: null,
      studio: null,          // active native studio: { instance, type, ready, failed, session, designStatus, lastSave }
      studioGlobalFailed: false,
      mockupRunId: 0,
      mockupFailures: {},
      basePreviewRunId: 0,
      cardMockups: {},       // handle -> objectURL
      basePreviewUrls: {},   // preview cache key -> objectURL
      basePreviewPromises: {},
      imagePromises: {},
      designs: [],
      designSeq: 0,
      started: false,
      modal: null,           // { handle, product, colorIndex, placementId, qty:{size:n}, viewUrls:[], activeView:0 }
      lastFocus: null
    };

    // ---- analytics (preserve GA4 vocabulary) -----------------------------
    var params = new URLSearchParams(window.location.search);
    var sourceParam = (params.get("source") || "").replace(/[^a-z0-9_-]/gi, "").slice(0, 40);
    var sourceSurface = sourceParam ? "email_campaign_" + sourceParam : "bulk_landing_page";

    function gtagSafe() {
      if (window.gtag) return window.gtag;
      window.dataLayer = window.dataLayer || [];
      return function () { window.dataLayer.push(arguments); };
    }
    var gtag = gtagSafe();

    function fire(name, extra) {
      try {
        var base = {
          item_count: state.designs.length,
          logo_uploaded: Boolean(state.logo),
          source_surface: sourceSurface,
          utm_campaign: params.get("utm_campaign") || undefined
        };
        var payload = Object.assign(base, extra || {});
        gtag("event", name, payload);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: name }, payload));
      } catch (err) { /* analytics must never interrupt the buyer */ }
    }

    function fireStartOnce() {
      if (state.started) return;
      state.started = true;
      fire("bulk_quote_start");
    }

    function notify(message) {
      if (!els.notice) return;
      if (!message) { els.notice.hidden = true; els.notice.textContent = ""; return; }
      els.notice.hidden = false;
      els.notice.textContent = message;
    }

    // ---- catalog ---------------------------------------------------------
    function productImage(product, colorIndex) {
      var colors = product.colors || [];
      var color = colors[colorIndex != null ? colorIndex : 0] || colors[0];
      return (color && color.imageUrl) || "";
    }

    function productColor(product, colorIndex) {
      var colors = product.colors || [];
      return colors[colorIndex != null ? colorIndex : 0] || colors[0] || null;
    }

    function cssImageUrl(value) {
      if (!value) return "";
      return 'url("' + String(value).replace(/["\\\n\r]/g, "") + '")';
    }

    function needsColorFill(product, color, imageUrl) {
      if (!product || !color || !imageUrl) return false;
      var colors = product.colors || [];
      if (String(color.imageKind || "").toLowerCase() === "ghost") return true;
      if (color.mockupBackgroundImageUrl) return true;
      if (colors.length < 2) return false;
      var urls = {};
      colors.forEach(function (item) { if (item && item.imageUrl) urls[item.imageUrl] = true; });
      return Object.keys(urls).length < colors.length;
    }

    function mockupHostStyle(product, color, imageUrl) {
      if (!needsColorFill(product, color, imageUrl)) return "";
      var fill = color.mockupBackgroundColor || color.hex || "#f4f4f4";
      return "--mfc-product-color:" + fill + ";" +
        "--mfc-product-texture:" + (color.mockupBackgroundImageUrl ? cssImageUrl(color.mockupBackgroundImageUrl) : "none") + ";" +
        "--mfc-product-mask:" + cssImageUrl(imageUrl) + ";";
    }

    function setProductFill(host, product, color, imageUrl) {
      if (!host) return;
      var style = mockupHostStyle(product, color, imageUrl);
      host.classList.toggle("has-product-fill", Boolean(style));
      if (!style) {
        host.style.removeProperty("--mfc-product-color");
        host.style.removeProperty("--mfc-product-texture");
        host.style.removeProperty("--mfc-product-mask");
        return;
      }
      host.style.cssText = host.style.cssText.replace(/--mfc-product-[^;]+;?/g, "");
      style.split(";").forEach(function (rule) {
        var parts = rule.split(":");
        if (parts.length >= 2) host.style.setProperty(parts[0], parts.slice(1).join(":"));
      });
    }

    function clearProductFill(host) {
      if (!host) return;
      host.classList.remove("has-product-fill");
      host.style.removeProperty("--mfc-product-color");
      host.style.removeProperty("--mfc-product-texture");
      host.style.removeProperty("--mfc-product-mask");
    }

    function imageLoad(src) {
      if (!src) return Promise.reject(new Error("Missing image"));
      if (state.imagePromises[src]) return state.imagePromises[src];
      state.imagePromises[src] = new Promise(function (resolve, reject) {
        var image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = function () { resolve(image); };
        image.onerror = function () { reject(new Error("Image failed to load")); };
        image.src = src;
      });
      return state.imagePromises[src];
    }

    function drawCover(ctx, image, width, height) {
      var iw = image.naturalWidth || image.width || width;
      var ih = image.naturalHeight || image.height || height;
      var scale = Math.max(width / iw, height / ih);
      var sw = width / scale;
      var sh = height / scale;
      var sx = Math.max(0, (iw - sw) / 2);
      var sy = Math.max(0, (ih - sh) / 2);
      ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
    }

    function hexToRgb(hex) {
      var h = String(hex || "").replace("#", "");
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      if (!/^[0-9a-f]{6}$/i.test(h)) return { r: 244, g: 244, b: 244 };
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16)
      };
    }

    function canvasToObjectUrl(canvas) {
      return new Promise(function (resolve) {
        if (canvas.toBlob) {
          canvas.toBlob(function (blob) {
            if (blob) resolve(URL.createObjectURL(blob));
            else resolve(canvas.toDataURL("image/png"));
          }, "image/png");
        } else {
          resolve(canvas.toDataURL("image/png"));
        }
      });
    }

    function productPreviewCacheKey(product, color, imageUrl) {
      return [product.handle, imageUrl, color && (color.mockupBackgroundColor || color.hex), color && color.mockupBackgroundImageUrl].join("|");
    }

    function getProductPreviewUrl(product, colorIndex) {
      var color = productColor(product, colorIndex);
      var imageUrl = productImage(product, colorIndex);
      if (!needsColorFill(product, color, imageUrl)) return Promise.resolve(imageUrl);

      var key = productPreviewCacheKey(product, color, imageUrl);
      if (state.basePreviewUrls[key]) return Promise.resolve(state.basePreviewUrls[key]);
      if (state.basePreviewPromises[key]) return state.basePreviewPromises[key];

      var texturePromise = color && color.mockupBackgroundImageUrl
        ? imageLoad(color.mockupBackgroundImageUrl).catch(function () { return null; })
        : Promise.resolve(null);

      state.basePreviewPromises[key] = Promise.all([imageLoad(imageUrl), texturePromise])
        .then(function (images) {
          var base = images[0];
          var texture = images[1];
          var maxDim = 900;
          var bw = base.naturalWidth || base.width || maxDim;
          var bh = base.naturalHeight || base.height || maxDim;
          var scale = Math.min(1, maxDim / Math.max(bw, bh));
          var width = Math.max(1, Math.round(bw * scale));
          var height = Math.max(1, Math.round(bh * scale));
          var canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          var ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas unavailable");

          var fill = hexToRgb((color && (color.mockupBackgroundColor || color.hex)) || "#f4f4f4");
          var textureData = null;
          if (texture) {
            var textureCanvas = document.createElement("canvas");
            textureCanvas.width = width;
            textureCanvas.height = height;
            var textureCtx = textureCanvas.getContext("2d");
            if (textureCtx) {
              drawCover(textureCtx, texture, width, height);
              textureData = textureCtx.getImageData(0, 0, width, height).data;
            }
          }

          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(base, 0, 0, width, height);
          var imageData = ctx.getImageData(0, 0, width, height);
          var data = imageData.data;

          for (var i = 0; i < data.length; i += 4) {
            var r = data[i];
            var g = data[i + 1];
            var b = data[i + 2];
            var a = data[i + 3];
            var luminance = (r + g + b) / 3;
            var darkness = Math.max(0, (255 - luminance) / 255);
            var alphaSignal = Math.max(0, (255 - a) / 255);
            var garment = Math.max(darkness * 3.15, alphaSignal * 1.05);

            if (garment < 0.035) {
              data[i] = 255;
              data[i + 1] = 255;
              data[i + 2] = 255;
              data[i + 3] = 0;
              continue;
            }

            garment = Math.min(1, garment);
            var sourceR = fill.r;
            var sourceG = fill.g;
            var sourceB = fill.b;
            if (textureData) {
              sourceR = textureData[i];
              sourceG = textureData[i + 1];
              sourceB = textureData[i + 2];
            }

            var shade = 0.74 + (luminance / 255) * 0.26;
            data[i] = Math.max(0, Math.min(255, Math.round(sourceR * shade)));
            data[i + 1] = Math.max(0, Math.min(255, Math.round(sourceG * shade)));
            data[i + 2] = Math.max(0, Math.min(255, Math.round(sourceB * shade)));
            data[i + 3] = Math.round(garment * 255);
          }

          ctx.clearRect(0, 0, width, height);
          ctx.putImageData(imageData, 0, 0);

          return canvasToObjectUrl(canvas);
        })
        .then(function (url) {
          state.basePreviewUrls[key] = url;
          return url;
        })
        .catch(function () { return imageUrl; });

      return state.basePreviewPromises[key];
    }

    function renderFilters() {
      if (!els.filters) return;
      var cats = [{ id: "all", name: "All products", count: state.products.length }].concat(state.categories);
      els.filters.innerHTML = cats.map(function (cat) {
        var count = num(cat.count != null ? cat.count : cat.productCount, 0);
        var active = state.activeCategory === cat.id ? " is-active" : "";
        return '<button class="mfc__filter' + active + '" type="button" role="tab" aria-selected="' +
          (active ? "true" : "false") + '" data-mfc-filter="' + escapeAttr(cat.id) + '">' +
          escapeHtml(cat.name) + '<span>' + count + '</span></button>';
      }).join("");
    }

    function cardHtml(product) {
      var img = state.cardMockups[product.handle] || productImage(product, 0);
      var hasLogoMockup = Boolean(state.cardMockups[product.handle]);
      var ready = product.catalogLoadState === "ready";
      return [
        '<article class="mfc__card" data-mfc-card="' + escapeAttr(product.handle) +
          '" data-category="' + escapeAttr(product.categoryId) +
          '" data-title="' + escapeAttr((product.title || "").toLowerCase()) + '">',
        '  <div class="mfc__card-media" data-mfc-card-media>',
        '    <img class="mfc__card-img" data-mfc-card-img alt="' + escapeAttr(product.title || "") + '"' +
              (img ? ' src="' + escapeAttr(img) + '"' : "") + '>',
        '    <span class="mfc__card-badge' + (hasLogoMockup ? " is-on" : "") + '" data-mfc-card-badge>Your logo</span>',
        '    <span class="mfc__card-spinner" data-mfc-card-spinner aria-hidden="true"></span>',
        '  </div>',
        '  <div class="mfc__card-body">',
        '    <span class="mfc__card-cat">' + escapeHtml(product.categoryName || "") + '</span>',
        '    <h3 class="mfc__card-title">' + escapeHtml(product.title || "") + '</h3>',
        '    <p class="mfc__card-price">' + escapeHtml(product.displayPriceLabel || "") +
              '<small>per item &middot; before bulk pricing</small></p>',
        '  </div>',
        '  <div class="mfc__card-foot">',
        '    <button class="mfc__btn mfc__btn--secondary mfc__btn--block mfc__btn--sm" type="button" data-mfc-design="' +
              escapeAttr(product.handle) + '"' + (ready ? "" : " disabled") + '>' +
              (ready ? "Design this item" : "Loading options\u2026") + '</button>',
        '  </div>',
        '</article>'
      ].join("");
    }

    function renderGrid() {
      if (!els.grid) return;
      if (!state.products.length) {
        els.grid.innerHTML = '<p class="mfc__catalog-empty">Catalog is loading\u2026</p>';
        return;
      }
      els.grid.innerHTML = state.products.map(cardHtml).join("");
      applyFilter();
      refreshBaseCardPreviews();
    }

    function applyFilter() {
      if (!els.grid) return;
      var cards = $all(els.grid, "[data-mfc-card]");
      var shown = 0;
      cards.forEach(function (card) {
        var matchCat = state.activeCategory === "all" || card.getAttribute("data-category") === state.activeCategory;
        var matchSearch = !state.search || card.getAttribute("data-title").indexOf(state.search) !== -1;
        var visible = matchCat && matchSearch;
        card.classList.toggle("is-hidden", !visible);
        if (visible) shown += 1;
      });
      if (els.resultCount) {
        els.resultCount.textContent = shown + (shown === 1 ? " product" : " products");
      }
      if (!shown && !els.grid.querySelector(".mfc__catalog-empty")) {
        var empty = document.createElement("p");
        empty.className = "mfc__catalog-empty";
        empty.setAttribute("data-mfc-empty", "");
        empty.textContent = "No products match that search.";
        els.grid.appendChild(empty);
      }
      var existingEmpty = els.grid.querySelector("[data-mfc-empty]");
      if (existingEmpty && shown) existingEmpty.remove();
    }

    function setProducts(catalog) {
      var products = (catalog && catalog.products) || [];
      if (!products.length) return false;
      state.products = products;
      state.categories = (catalog && catalog.categories) || [];
      state.byHandle = {};
      products.forEach(function (p) { state.byHandle[p.handle] = p; });
      state.hydrated = catalog.catalogLoadState === "ready";
      renderFilters();
      renderGrid();
      return true;
    }

    function loadCatalog() {
      if (els.catalogStatus) { els.catalogStatus.hidden = false; els.catalogStatus.textContent = "Loading Makefield products\u2026"; }
      // Shell first for instant cards, then the hydrated catalog with full options.
      getJson(buildUrl(endpoints.catalog, { scope: "shell" }))
        .then(function (body) {
          var catalog = body.catalog || body;
          if (setProducts(catalog)) {
            if (els.catalogStatus) els.catalogStatus.hidden = true;
            fire("bulk_catalog_shell_view", { product_count: state.products.length });
          }
        })
        .catch(function () { /* shell is best-effort; full load follows */ })
        .then(function () {
          return getJson(endpoints.catalog);
        })
        .then(function (body) {
          var catalog = body.catalog || body;
          if (setProducts(catalog)) {
            if (els.catalogStatus) els.catalogStatus.hidden = true;
            fire("bulk_catalog_view", { product_count: state.products.length });
            if (state.logo) refreshCardMockups();
          } else if (!state.products.length && els.catalogStatus) {
            els.catalogStatus.hidden = false;
            els.catalogStatus.textContent = "Catalog is unavailable right now. You can still describe what you need below.";
          }
        })
        .catch(function () {
          if (!state.products.length && els.catalogStatus) {
            els.catalogStatus.hidden = false;
            els.catalogStatus.textContent = "Catalog is unavailable right now. You can still describe what you need below.";
          }
        });
    }

    // ---- embed mockup SDK ------------------------------------------------
    function waitForSdkGlobal() {
      if (window.MakefieldFyul && window.MakefieldFyul.Embed) return Promise.resolve();
      return new Promise(function (resolve, reject) {
        var started = Date.now();
        var timer = window.setInterval(function () {
          if (window.MakefieldFyul && window.MakefieldFyul.Embed) {
            window.clearInterval(timer); resolve();
          } else if (Date.now() - started > SDK_TIMEOUT_MS) {
            window.clearInterval(timer); reject(new Error("Design preview is taking a moment to load."));
          }
        }, 80);
      });
    }

    function ensureSdk() {
      if (state.sdk) return Promise.resolve(state.sdk);
      if (state.sdkPromise) return state.sdkPromise;
      state.sdkPromise = waitForSdkGlobal()
        .then(function () { return postJson(endpoints.ewlSession, {}); })
        .then(function (session) {
          var token = session.token || session.jwt;
          if (!token) throw new Error("No preview session token");
          var sdk = new window.MakefieldFyul.Embed(token, { debug: false });
          return Promise.resolve(sdk.init()).then(function () { state.sdk = sdk; return sdk; });
        })
        .catch(function (err) {
          state.sdkPromise = null;
          throw err;
        });
      return state.sdkPromise;
    }

    function fileNameForBlob(baseName, contentType) {
      var name = String(baseName || "logo").trim() || "logo";
      var lower = String(contentType || "").toLowerCase();
      var ext = lower.indexOf("svg") !== -1 ? ".svg" :
        lower.indexOf("jpeg") !== -1 || lower.indexOf("jpg") !== -1 ? ".jpg" :
        lower.indexOf("webp") !== -1 ? ".webp" : ".png";
      if (/\.(svg|png|jpe?g|webp)$/i.test(name)) return name;
      return name + ext;
    }

    // Returns an SDK-safe File for the active logo (normalized PNG when available).
    function getLogoFile() {
      var logo = state.logo;
      if (!logo) return Promise.reject(new Error("No logo"));
      var isSvg = (logo.contentType || "").indexOf("svg") !== -1 || /\.svg$/i.test(logo.fileName || "");
      if (logo.sdkFile && (!isSvg || logo.sdkFileFromPublicUrl === logo.publicImageUrl || !logo.publicImageUrl)) {
        return Promise.resolve(logo.sdkFile);
      }
      if (logo.file && !isSvg) { logo.sdkFile = logo.file; return Promise.resolve(logo.file); }
      if (logo.publicImageUrl) {
        return fetch(logo.publicImageUrl, { mode: "cors" })
          .then(function (res) { if (!res.ok) throw new Error("logo fetch failed"); return res.blob(); })
          .then(function (blob) {
            var file = new File([blob], fileNameForBlob(logo.fileName, blob.type || logo.contentType), { type: blob.type || logo.contentType || "image/png" });
            logo.sdkFile = file;
            logo.sdkFileFromPublicUrl = logo.publicImageUrl;
            return file;
          });
      }
      if (isSvg && logo.publicUrlPromise) {
        return logoPublicUrlReady().then(function (url) {
          if (url) {
            logo.publicImageUrl = url;
            logo.sdkFile = null;
            return getLogoFile();
          }
          if (logo.file) return logo.file;
          throw new Error("No usable logo file");
        });
      }
      if (logo.file) { logo.sdkFile = logo.file; return Promise.resolve(logo.file); }
      return Promise.reject(new Error("No usable logo file"));
    }

    function positiveNumber(value) {
      var n = Number(value);
      return Number.isFinite(n) && n > 0 ? n : null;
    }

    function selectedVariantIdForProduct(product, colorIndex) {
      var color = productColor(product, colorIndex || 0);
      var variantId = positiveNumber(color && color.printfulCatalogVariantId);
      var sizes = (color && color.sizes) || [];
      for (var i = 0; !variantId && i < sizes.length; i += 1) {
        variantId = positiveNumber(sizes[i] && sizes[i].printfulCatalogVariantId);
      }
      return variantId || positiveNumber(product && product.printfulCatalogVariantId);
    }

    function selectedTechniqueForProduct(product) {
      var raw = String((product && product.finishingType) || "").toLowerCase().replace(/_/g, "-").trim();
      if (!raw || raw === "print" || raw === "dtg" || raw.indexOf("direct-to-garment") !== -1) return "DTG";
      if (raw.indexOf("embroid") !== -1) return "EMBROIDERY";
      if (raw.indexOf("sublim") !== -1 || raw.indexOf("dye-sublimation") !== -1) return "SUBLIMATION";
      if (raw === "dtf" || raw.indexOf("direct-to-film") !== -1) return "DTFILM";
      return null;
    }

    function selectedMockupStyleForProduct(product, colorIndex) {
      var color = productColor(product, colorIndex || 0);
      var styleId = positiveNumber(color && color.preferredMockupStyleId);
      return {
        id: styleId,
        placement: (color && color.preferredMockupPlacement) || product.defaultPlacementId || null,
        label: (color && color.preferredMockupStyleLabel) || null,
        kind: (color && color.imageKind) || null
      };
    }

    function buildNativeMockupProductArg(product, colorIndex) {
      var productId = positiveNumber(product && product.printfulCatalogProductId);
      if (!productId) return null;
      var variantId = selectedVariantIdForProduct(product, colorIndex || 0);
      if (!variantId) return null;
      var arg = {
        productId: productId,
        variantIds: [variantId],
        selectedVariantId: variantId
      };
      var technique = selectedTechniqueForProduct(product);
      if (technique) arg.selectedTechnique = technique;
      var style = selectedMockupStyleForProduct(product, colorIndex || 0);
      if (style.placement) {
        arg.selectedPlacement = style.placement;
        arg.placementId = style.placement;
        arg.placement = style.placement;
        arg.preferredMockupPlacement = style.placement;
      }
      if (style.label) arg.preferredMockupStyleLabel = style.label;
      if (style.kind) arg.preferredMockupKind = style.kind;
      if (style.id) {
        arg.mockupStyleId = style.id;
        arg.mockup_style_id = style.id;
        arg.mockupStyleIds = [style.id];
        arg.mockup_style_ids = [style.id];
        arg.preferredMockupStyleId = style.id;
      }
      return arg;
    }

    function nativeMockupRequestKey(arg) {
      return [arg && arg.productId, arg && arg.selectedVariantId, arg && arg.preferredMockupStyleId].join("|");
    }

    function nativeMockupResultVariantId(mockup) {
      return positiveNumber(mockup && (mockup.variantId || mockup.selectedVariantId || mockup.catalogVariantId || mockup.productVariantId));
    }

    function nativeMockupResultStyleId(mockup) {
      return positiveNumber(mockup && (mockup.mockupStyleId || mockup.mockup_style_id || mockup.styleId || mockup.style_id));
    }

    function nativeMockupText(mockup) {
      return [
        mockup && mockup.label,
        mockup && mockup.title,
        mockup && mockup.view,
        mockup && mockup.viewName,
        mockup && mockup.view_name,
        mockup && mockup.placement,
        mockup && mockup.category,
        mockup && mockup.categoryName,
        mockup && mockup.category_name
      ].filter(Boolean).join(" ").toLowerCase();
    }

    function nativeMockupStyleScore(mockup, arg) {
      var score = 0;
      var styleId = nativeMockupResultStyleId(mockup);
      if (arg.preferredMockupStyleId) {
        if (styleId === arg.preferredMockupStyleId) score -= 80;
        else if (styleId) score += 80;
      }
      var text = nativeMockupText(mockup);
      var kind = String(arg.preferredMockupKind || "").toLowerCase();
      if (kind && text.indexOf(kind) !== -1) score -= 20;
      if (arg.preferredMockupPlacement && text.indexOf(String(arg.preferredMockupPlacement).toLowerCase()) !== -1) score -= 10;
      if (text.indexOf("ghost") !== -1) score -= 6;
      else if (text.indexOf("flat") !== -1) score -= 4;
      if (text.indexOf("model") !== -1 || text.indexOf("lifestyle") !== -1) score += 20;
      return score;
    }

    function previewFailureMessage(count) {
      if (count === 1) return "Exact Printful logo preview is unavailable for 1 product. We are not showing approximate mockups.";
      return "Exact Printful logo previews are unavailable for " + count + " products. We are not showing approximate mockups.";
    }

    function renderMockupFailureNotice() {
      var count = Object.keys(state.mockupFailures || {}).length;
      if (count) notify(previewFailureMessage(count));
      else notify("");
    }

    function reasonText(reason) {
      if (!reason) return "Printful did not return a usable mockup.";
      if (typeof reason === "string") return reason;
      return reason.message || reason.error || "Printful did not return a usable mockup.";
    }

    function setCardSpinner(handle, on) {
      var card = els.grid && els.grid.querySelector('[data-mfc-card="' + cssEscape(handle) + '"]');
      if (!card) return;
      var spinner = card.querySelector("[data-mfc-card-spinner]");
      var img = card.querySelector("[data-mfc-card-img]");
      if (spinner) spinner.classList.toggle("is-on", on);
      if (img) img.classList.toggle("is-loading", on);
    }

    function setCardMockup(handle, objectUrl) {
      var prev = state.cardMockups[handle];
      if (prev && prev !== objectUrl) { try { URL.revokeObjectURL(prev); } catch (e) {} }
      state.cardMockups[handle] = objectUrl;
      var card = els.grid && els.grid.querySelector('[data-mfc-card="' + cssEscape(handle) + '"]');
      if (!card) return;
      clearProductFill(card.querySelector("[data-mfc-card-media]"));
      var img = card.querySelector("[data-mfc-card-img]");
      var badge = card.querySelector("[data-mfc-card-badge]");
      if (img) { img.src = objectUrl; img.classList.remove("is-loading"); }
      if (badge) {
        badge.textContent = "Your logo";
        badge.removeAttribute("title");
        badge.classList.add("is-on");
        badge.classList.remove("is-error");
      }
      card.classList.remove("has-preview-error");
      if (state.mockupFailures[handle]) {
        delete state.mockupFailures[handle];
        renderMockupFailureNotice();
      }
    }

    function updateBaseCardPreview(product, runId) {
      if (!product || state.cardMockups[product.handle]) return;
      getProductPreviewUrl(product, 0).then(function (url) {
        if (runId !== state.basePreviewRunId || state.cardMockups[product.handle]) return;
        var card = els.grid && els.grid.querySelector('[data-mfc-card="' + cssEscape(product.handle) + '"]');
        var img = card && card.querySelector("[data-mfc-card-img]");
        if (img && url) img.src = url;
      });
    }

    function refreshBaseCardPreviews() {
      if (!els.grid || !state.products.length) return;
      var runId = ++state.basePreviewRunId;
      state.products.forEach(function (product) { updateBaseCardPreview(product, runId); });
    }

    function clearCardMockups() {
      Object.keys(state.cardMockups).forEach(function (h) {
        try { URL.revokeObjectURL(state.cardMockups[h]); } catch (e) {}
      });
      state.cardMockups = {};
      state.mockupFailures = {};
      if (!els.grid) return;
      $all(els.grid, "[data-mfc-card]").forEach(function (card) {
        var handle = card.getAttribute("data-mfc-card");
        var product = state.byHandle[handle];
        var baseImage = product && productImage(product, 0);
        clearProductFill(card.querySelector("[data-mfc-card-media]"));
        card.classList.remove("has-preview-error");
        var img = card.querySelector("[data-mfc-card-img]");
        var badge = card.querySelector("[data-mfc-card-badge]");
        if (img && baseImage) img.src = baseImage;
        if (badge) {
          badge.textContent = "Your logo";
          badge.removeAttribute("title");
          badge.classList.remove("is-on");
          badge.classList.remove("is-error");
        }
        if (product) updateBaseCardPreview(product, state.basePreviewRunId);
      });
    }

    function markCardMockupUnavailable(product, runId, reason) {
      if (!product || runId !== state.mockupRunId) return;
      var handle = product.handle;
      var failureReason = reasonText(reason);
      var prev = state.cardMockups[handle];
      if (prev) { try { URL.revokeObjectURL(prev); } catch (e) {} }
      delete state.cardMockups[handle];
      setCardSpinner(handle, false);
      var card = els.grid && els.grid.querySelector('[data-mfc-card="' + cssEscape(handle) + '"]');
      if (card) {
        var baseImage = productImage(product, 0);
        clearProductFill(card.querySelector("[data-mfc-card-media]"));
        card.classList.add("has-preview-error");
        var img = card.querySelector("[data-mfc-card-img]");
        var badge = card.querySelector("[data-mfc-card-badge]");
        if (img && baseImage) {
          img.src = baseImage;
          img.classList.remove("is-loading");
        }
        if (badge) {
          badge.textContent = "Preview unavailable";
          badge.title = failureReason;
          badge.classList.add("is-on");
          badge.classList.add("is-error");
        }
        updateBaseCardPreview(product, state.basePreviewRunId);
      }
      state.mockupFailures[handle] = failureReason;
      renderMockupFailureNotice();
      if (window.console && window.console.warn) {
        window.console.warn("[Makefield bulk] Exact Printful card preview unavailable for " + handle + ": " + failureReason, {
          handle: handle,
          reason: failureReason
        });
      }
    }

    // Primary: native logo-applied mockups via the embed SDK getMockups API.
    function refreshCardMockups() {
      if (!state.logo || !state.hydrated) return;
      var runId = ++state.mockupRunId;
      state.mockupFailures = {};
      notify("");
      state.products.forEach(function (p) { setCardSpinner(p.handle, true); });
      if (!state.products.length) return;

      Promise.all([ensureSdk(), getLogoFile()])
        .then(function (parts) {
          if (runId !== state.mockupRunId) return;
          var sdk = parts[0];
          var file = parts[1];
          return runNativeCardChunk(sdk, file, state.products, 0, runId);
        })
        .catch(function (err) {
          if (runId !== state.mockupRunId) return;
          state.products.forEach(function (p) { markCardMockupUnavailable(p, runId, err); });
        });
    }

    function applyNativeCardMockupResult(result, requests, runId) {
      if (result && result.error) throw new Error(reasonText(result.error));
      var data = (result && result.data) || result || {};
      var mockups = data.mockups || [];
      var productIdCounts = {};
      requests.forEach(function (request) {
        productIdCounts[String(request.arg.productId)] = (productIdCounts[String(request.arg.productId)] || 0) + 1;
      });
      var misses = [];
      requests.forEach(function (request) {
        var candidates = mockups.filter(function (mockup) {
          if (!mockup || !mockup.blob || mockup.failed) return false;
          var productId = positiveNumber(mockup.productId);
          if (productId && productId !== request.arg.productId) return false;
          var variantId = nativeMockupResultVariantId(mockup);
          if (variantId && variantId !== request.arg.selectedVariantId) return false;
          if (!variantId && productId && productIdCounts[String(productId)] !== 1) return false;
          return true;
        }).sort(function (a, b) {
          return nativeMockupStyleScore(a, request.arg) - nativeMockupStyleScore(b, request.arg);
        });
        if (candidates.length) {
          setCardMockup(request.product.handle, URL.createObjectURL(candidates[0].blob));
          setCardSpinner(request.product.handle, false);
        } else {
          misses.push(request);
        }
      });
      return misses;
    }

    function retryNativeCardRequests(sdk, file, requests, runId) {
      if (runId !== state.mockupRunId || !requests.length) return Promise.resolve();
      return Promise.all(requests.map(function (request) {
        if (runId !== state.mockupRunId) return Promise.resolve();
        return Promise.resolve(sdk.mockup.getMockups({ file: file, products: [request.arg] }))
          .then(function (result) {
            if (runId !== state.mockupRunId) return;
            var misses = applyNativeCardMockupResult(result, [request], runId);
            if (misses.length) markCardMockupUnavailable(request.product, runId, "Printful did not return a usable exact mockup.");
          })
          .catch(function (err) {
            if (runId !== state.mockupRunId) return;
            markCardMockupUnavailable(request.product, runId, err);
          });
      })).then(function () {});
    }

    function runNativeCardChunk(sdk, file, products, start, runId) {
      if (runId !== state.mockupRunId || start >= products.length) return Promise.resolve();
      var chunk = products.slice(start, start + MOCKUP_CHUNK);
      var requests = [];
      chunk.forEach(function (product) {
        var arg = buildNativeMockupProductArg(product, 0);
        if (!arg) {
          markCardMockupUnavailable(product, runId, "Missing Printful product or variant mapping.");
          return;
        }
        requests.push({ product: product, arg: arg, key: nativeMockupRequestKey(arg) });
      });
      if (!requests.length) return runNativeCardChunk(sdk, file, products, start + MOCKUP_CHUNK, runId);

      return Promise.resolve(sdk.mockup.getMockups({ file: file, products: requests.map(function (r) { return r.arg; }) }))
        .then(function (result) {
          if (runId !== state.mockupRunId) return;
          var misses = applyNativeCardMockupResult(result, requests, runId);
          return retryNativeCardRequests(sdk, file, misses, runId);
        })
        .catch(function (err) {
          if (runId !== state.mockupRunId) return;
          return retryNativeCardRequests(sdk, file, requests, runId);
        })
        .then(function () {
          return runNativeCardChunk(sdk, file, products, start + MOCKUP_CHUNK, runId);
        });
    }

    function cssEscape(value) {
      if (window.CSS && window.CSS.escape) return window.CSS.escape(value);
      return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
    }

    // ---- logo handling ---------------------------------------------------
    function renderLogoState() {
      var has = Boolean(state.logo);
      if (els.logoDrop) els.logoDrop.classList.toggle("is-on", has);
      if (els.logoPreview) els.logoPreview.classList.toggle("is-on", has);
      if (has) {
        if (els.logoName) els.logoName.textContent = state.logo.fileName || "Your logo";
        var src = state.logo.previewUrl || state.logo.publicImageUrl || "";
        if (els.logoThumb && src) els.logoThumb.style.backgroundImage = 'url("' + src + '")';
      } else if (els.logoThumb) {
        els.logoThumb.style.backgroundImage = "";
      }
    }

    function setLogoStatus(message) { if (els.logoStatus) els.logoStatus.textContent = message || ""; }

    function applyLogo(logo, statusMessage) {
      state.logo = logo;
      clearCardMockups();
      renderLogoState();
      if (statusMessage) setLogoStatus(statusMessage);
      // Re-run mockups with the new logo.
      state.mockupRunId += 1;
      if (state.hydrated && state.products.length) {
        refreshCardMockups();
      }
      if (state.modal && !state.studio) loadModalPreview();
    }

    function handleLogoFile(file) {
      if (!file) return;
      if (!/^image\//.test(file.type) && !/\.svg$/i.test(file.name || "")) {
        setLogoStatus("Please choose an image file (PNG, JPG, SVG, or WEBP).");
        return;
      }
      fireStartOnce();
      state.storeGraphic = null;
      var uploadedSvg = (file.type || "").indexOf("svg") !== -1 || /\.svg$/i.test(file.name || "");
      var previewUrl = URL.createObjectURL(file);
      var resolvePublicUrl;
      var publicUrlPromise = new Promise(function (resolve) { resolvePublicUrl = resolve; });
      var logo = {
        source: "uploaded",
        file: file,
        fileName: file.name || "Your logo",
        contentType: file.type || null,
        publicImageUrl: null,
        assetId: null,
        sha256: null,
        previewUrl: previewUrl,
        publicUrlPromise: publicUrlPromise
      };
      applyLogo(logo, "Preparing your logo\u2026");
      fire("bulk_logo_upload", { logo_type: file.type || "unknown" });

      var formData = new FormData();
      formData.append("file", file);
      fetch(endpoints.logoAssets, { method: "POST", body: formData })
        .then(function (res) { return res.json().then(function (body) {
          if (!res.ok || body.ok === false) throw new Error(body.error || "Could not prepare logo.");
          return body;
        }); })
        .then(function (body) {
          if (state.logo === logo) {
            state.logo.publicImageUrl = body.publicImageUrl || null;
            state.logo.assetId = body.assetId || null;
            state.logo.sha256 = body.sha256 || null;
            if (uploadedSvg) state.logo.sdkFile = null;
            setLogoStatus("Logo ready \u2014 preview it on the products below.");
            if (uploadedSvg && state.hydrated && state.products.length) refreshCardMockups();
            if (uploadedSvg && state.modal && !state.studio) loadModalPreview();
          }
          resolvePublicUrl(body.publicImageUrl || null);
        })
        .catch(function () {
          setLogoStatus("Logo added to your request. We'll clean it up before production.");
          resolvePublicUrl(null);
        });
    }

    // Resolves with a public HTTPS logo URL once normalization finishes (or null).
    function logoPublicUrlReady() {
      var logo = state.logo;
      if (!logo) return Promise.resolve(null);
      if (logo.publicImageUrl) return Promise.resolve(logo.publicImageUrl);
      if (logo.publicUrlPromise) {
        return Promise.race([
          logo.publicUrlPromise,
          new Promise(function (resolve) { window.setTimeout(function () { resolve(logo.publicImageUrl || null); }, 9000); })
        ]).then(function (url) { return url || logo.publicImageUrl || null; });
      }
      return Promise.resolve(logo.publicImageUrl || null);
    }

    function clearLogo() {
      if (state.logo && state.logo.previewUrl) { try { URL.revokeObjectURL(state.logo.previewUrl); } catch (e) {} }
      state.logo = null;
      state.storeGraphic = null;
      state.mockupRunId += 1;
      renderLogoState();
      setLogoStatus("PNG, JPG, SVG, or WEBP. Drag & drop works too.");
      clearCardMockups();
      notify("");
      if (state.modal && !state.studio) loadModalPreview();
      if (els.logoInput) els.logoInput.value = "";
      $all(els.storeGraphics, ".mfc__graphic").forEach(function (b) { b.classList.remove("is-selected"); });
    }

    // ---- store graphic picker -------------------------------------------
    function searchStores() {
      var query = els.storeSearch ? els.storeSearch.value.trim() : "";
      if (query.length < 2) { if (els.storeStatus) els.storeStatus.textContent = "Type at least 2 characters."; return; }
      if (els.storeStatus) els.storeStatus.textContent = "Searching stores\u2026";
      getJson(buildUrl(endpoints.storeSearch, { query: query }))
        .then(function (body) {
          state.storeResults = body.stores || body.results || [];
          renderStoreResults();
          fire("bulk_store_logo_search", { search_term: query.slice(0, 80), result_count: state.storeResults.length });
        })
        .catch(function () { if (els.storeStatus) els.storeStatus.textContent = "Store search is unavailable right now."; });
    }

    function renderStoreResults() {
      if (!els.storeResults) return;
      if (!state.storeResults.length) {
        els.storeResults.innerHTML = '<p class="mfc__store-empty">No stores found.</p>';
        if (els.storeStatus) els.storeStatus.textContent = "";
        return;
      }
      if (els.storeStatus) els.storeStatus.textContent = "Choose a store to see its graphics.";
      els.storeResults.innerHTML = state.storeResults.map(function (store) {
        return '<button class="mfc__store-result" type="button" data-mfc-store-handle="' + escapeAttr(store.handle || "") + '">' +
          '<strong>' + escapeHtml(store.name || store.handle || "Store") + '</strong>' +
          '<span>' + escapeHtml([store.city, store.state].filter(Boolean).join(", ")) + '</span></button>';
      }).join("");
    }

    function loadStoreGraphics(handle) {
      if (!handle) return;
      if (els.storeStatus) els.storeStatus.textContent = "Loading graphics\u2026";
      getJson(endpoints.storeSearch.replace(/\/$/, "") + "/" + encodeURIComponent(handle) + "/graphics")
        .then(function (body) {
          state.storeGraphicsList = body.graphics || [];
          renderStoreGraphics(handle);
          fire("bulk_store_logo_graphics_view", { collection_handle: handle, graphic_count: state.storeGraphicsList.length });
        })
        .catch(function () { if (els.storeStatus) els.storeStatus.textContent = "Could not load graphics for that store."; });
    }

    function storeGraphicPrintUrl(graphic) {
      return (graphic && (graphic.printImageUrl || graphic.publicImageUrl)) || null;
    }

    function storeGraphicUnavailableReason(graphic) {
      return (graphic && graphic.unavailableReason) ||
        "This store graphic does not have a cleaned no-background source yet, so we are not using it for Printful mockups.";
    }

    function storeGraphicIsPrintReady(graphic) {
      var printUrl = storeGraphicPrintUrl(graphic);
      if (!printUrl) return false;
      if (graphic.printReady === false || graphic.isPrintReady === false) return false;
      if (!graphic.printImageUrl && graphic.source === "original_upload" && !/svg/i.test(graphic.mimeType || graphic.fileName || "")) {
        return false;
      }
      return true;
    }

    function renderStoreGraphics(handle) {
      if (!els.storeGraphics) return;
      if (!state.storeGraphicsList.length) {
        els.storeGraphics.innerHTML = '<p class="mfc__store-empty">No graphics found for this store.</p>';
        return;
      }
      if (els.storeStatus) els.storeStatus.textContent = "Choose a graphic to use as your logo.";
      els.storeGraphics.innerHTML = state.storeGraphicsList.map(function (graphic) {
        var id = graphic.assetId || graphic.graphicId || "";
        var preview = graphic.previewUrl || graphic.publicImageUrl || "";
        var label = graphic.label || "Store graphic";
        var ready = storeGraphicIsPrintReady(graphic);
        var reason = storeGraphicUnavailableReason(graphic);
        return '<button class="mfc__graphic' + (ready ? "" : " is-unavailable") + '" type="button" title="' + escapeAttr(ready ? label : reason) +
          '" aria-label="' + escapeAttr(label) +
          '" aria-disabled="' + (ready ? "false" : "true") +
          '" data-mfc-graphic-id="' + escapeAttr(id) + '" data-mfc-store-handle="' + escapeAttr(handle) + '">' +
          (preview ? '<img class="mfc__graphic-img" src="' + escapeAttr(preview) + '" alt="" loading="lazy">' : "") +
          '<span class="mfc__graphic-label">' + escapeHtml(label) + '</span>' +
          (ready ? "" : '<span class="mfc__graphic-status">Needs cleanup</span>') + '</button>';
      }).join("");
    }

    function selectStoreGraphic(handle, graphicId) {
      var graphic = state.storeGraphicsList.filter(function (item) {
        return String(item.assetId || item.graphicId || "") === String(graphicId || "");
      })[0];
      if (!graphic) return;
      if (!storeGraphicIsPrintReady(graphic)) {
        var reason = storeGraphicUnavailableReason(graphic);
        if (els.storeStatus) els.storeStatus.textContent = reason;
        notify(reason);
        fire("bulk_store_logo_unavailable", { collection_handle: handle || null, graphic_id: graphicId });
        return;
      }
      fireStartOnce();
      var publicUrl = storeGraphicPrintUrl(graphic);
      var previewUrl = graphic.previewUrl || publicUrl;
      state.storeGraphic = {
        graphicId: graphic.assetId || graphic.graphicId || null,
        organizationId: graphic.organizationId || null,
        organizationName: graphic.organizationName || graphic.storeName || null,
        collectionHandle: handle || graphic.collectionHandle || null,
        label: graphic.label || null,
        publicImageUrl: publicUrl,
        previewUrl: previewUrl,
        source: graphic.source || "store_graphic"
      };
      $all(els.storeGraphics, ".mfc__graphic").forEach(function (b) {
        b.classList.toggle("is-selected", b.getAttribute("data-mfc-graphic-id") === String(graphicId));
      });
      applyLogo({
        source: "store_graphic",
        file: null,
        fileName: graphic.fileName || graphic.label || "Store graphic",
        contentType: graphic.mimeType || (/\.svg$/i.test(graphic.fileName || "") ? "image/svg+xml" : "image/png"),
        publicImageUrl: publicUrl,
        assetId: state.storeGraphic.graphicId,
        sha256: null,
        previewUrl: previewUrl
      }, "Using your store graphic \u2014 preview it on the products below.");
      fire("bulk_store_logo_select", { collection_handle: handle || null, graphic_id: graphicId });
    }

    // ---- native design studio (in modal) --------------------------------
    function studioGlobalAvailable() {
      return Boolean(window.MakefieldFyul && (window.MakefieldFyul.EDM || window.MakefieldFyul.EmbedStudio));
    }

    function studioVisible(on) {
      if (els.studioBar) els.studioBar.hidden = !on;
      if (els.studio) els.studio.hidden = !on;
      if (els.stage) els.stage.style.display = on ? "none" : "";
      if (els.views) els.views.style.display = on ? "none" : "";
    }

    function setStudioLoading(on, text) {
      if (els.studioLoading) els.studioLoading.hidden = !on;
      if (text && els.studioLoadingText) els.studioLoadingText.textContent = text;
    }

    function destroyStudio() {
      var s = state.studio;
      state.studio = null;
      if (s && s.instance) { try { s.instance.destroy(); } catch (e) {} }
      if (els.studio) {
        $all(els.studio, "iframe").forEach(function (frame) { try { frame.remove(); } catch (e) {} });
      }
      studioVisible(false);
    }

    // Primary design experience: mount the native product studio with the
    // buyer's logo auto-applied. If studio launch fails, the modal uses the
    // same exact SDK mockup path as the product cards.
    function mountStudio(m) {
      if (!els.studio || state.studioGlobalFailed || !studioGlobalAvailable() || !m.product.printfulCatalogProductId) {
        loadModalPreview();
        return;
      }
      studioVisible(true);
      setStudioLoading(true, "Opening the Makefield Design Studio\u2026");
      if (els.placementWrap) els.placementWrap.hidden = true;
      if (els.previewNote) els.previewNote.textContent = "";

      var product = m.product;
      var color = (product.colors || [])[m.colorIndex] || null;
      var studio = {
        instance: null, type: null, ready: false, failed: false,
        session: null, designStatus: null, pricingStatus: null, lastSave: null, logoPublicUrl: null
      };
      state.studio = studio;
      renderModal();
      fire("bulk_design_studio_open", { item_id: m.handle });

      logoPublicUrlReady()
        .then(function (publicUrl) {
          if (state.studio !== studio || state.modal !== m) return null;
          studio.logoPublicUrl = publicUrl || (state.logo && state.logo.publicImageUrl) || null;
          return postJson(endpoints.embedSession, {
            experience: "edm",
            productHandle: product.handle,
            clientSessionId: state.clientSessionId,
            selectedColor: color ? color.color : null,
            selectedSize: null,
            selectedPlacement: m.placementId || null,
            logoAssetId: (state.logo && state.logo.assetId) || null,
            logoPublicImageUrl: publicUrl || (state.logo && state.logo.publicImageUrl) || null,
            externalCustomerId: null
          });
        })
        .then(function (session) {
          if (!session || state.studio !== studio || state.modal !== m) return null;
          studio.session = session;
          return initStudioInstance(studio, session, m);
        })
        .catch(function (err) { failStudio(studio, m, err); });
    }

    function initStudioInstance(studio, session, m) {
      var container = els.studio;
      // The SDK structured-clones params into its setup postMessage, so we must
      // pass a string elementId (never a DOM node) to mount the studio iframe.
      if (!container.id) container.id = "mfc-studio-host-" + state.clientSessionId;
      var listeners = {
        onDesignStatusUpdate: function (payload) {
          studio.designStatus = payload;
          updateModalRunning();
        },
        onStepStatusUpdate: function () {},
        onAnalyticsUpdate: function () {},
        onFilePickerRequested: function () { if (els.logoInput) els.logoInput.click(); },
        onError: function (err) { failStudio(studio, m, err); }
      };
      var cfg = session.edmConfig || { params: {}, config: {} };
      var params = Object.assign({}, cfg.params, { elementId: container.id, iframeClassName: "mfc__studio-frame" });
      var config = Object.assign({}, cfg.config, { locale: "en_US" });
      var instance;
      try {
        if (session.authType === "jwt" && window.MakefieldFyul.EmbedStudio) {
          if (cfg.params && cfg.params.embedStudio) Object.assign(params, cfg.params.embedStudio);
          params.jwt = session.token;
          studio.type = "embed_studio";
          instance = new window.MakefieldFyul.EmbedStudio(params, config, listeners);
        } else if (window.MakefieldFyul.EDM) {
          params.nonce = session.token;
          studio.type = "edm";
          instance = new window.MakefieldFyul.EDM(params, config, listeners);
        } else {
          throw new Error("No studio constructor available");
        }
      } catch (err) { failStudio(studio, m, err); return null; }
      studio.instance = instance;

      var initPromise = Promise.resolve(instance.init());
      var timeout = new Promise(function (_, reject) {
        window.setTimeout(function () { reject(new Error("Studio timed out")); }, STUDIO_TIMEOUT_MS);
      });
      return Promise.race([initPromise, timeout])
        .then(function (res) {
          if (state.studio !== studio) { try { instance.destroy(); } catch (e) {} return; }
          if (res && res.error) throw res.error;
          studio.ready = true;
          setStudioLoading(false);
          // Push the buyer's logo into the studio so it is one tap from any print
          // area. FYUL/EDM builds have used both `imageUrl` and `url`; send both
          // so the selected Makefield/store logo reaches whichever bridge is active.
          if (studio.logoPublicUrl && instance.addImage) {
            try {
              Promise.resolve(instance.addImage({
                imageUrl: studio.logoPublicUrl,
                url: studio.logoPublicUrl
              })).catch(function () {});
            } catch (e) {}
          }
          fire("bulk_design_studio_ready", { item_id: m.handle, studio_type: studio.type });
        })
        .catch(function (err) { failStudio(studio, m, err); });
    }

    function failStudio(studio, m, err) {
      state.studioGlobalFailed = state.studioGlobalFailed || !studioGlobalAvailable();
      if (state.studio === studio) {
        if (studio.instance) { try { studio.instance.destroy(); } catch (e) {} studio.instance = null; }
        studio.failed = true;
        state.studio = null;
      } else if (studio && studio.instance) {
        try { studio.instance.destroy(); } catch (e) {}
      }
      if (state.modal === m) {
        studioVisible(false);
        if (els.placementWrap) els.placementWrap.hidden = (m.product.placements || []).length < 2;
        renderModal();
        loadModalPreview();
      }
      if (err) {
        fire("bulk_design_studio_fallback", {
          item_id: m.handle,
          error_message: String((err && err.message) || err).slice(0, 120)
        });
      }
    }

    function pickProp(obj, keys) {
      if (!obj) return null;
      for (var i = 0; i < keys.length; i += 1) {
        if (obj[keys[i]] !== undefined && obj[keys[i]] !== null) return obj[keys[i]];
      }
      return null;
    }

    function normalizeNativeDesign(data, studio) {
      var template = data.template || data.savedProduct || data.product || data;
      var mockups = data.mockups || data.previews || template.mockups || [];
      var firstMockup = Array.isArray(mockups) && mockups.length ? mockups[0] : null;
      var mockupUrl = (firstMockup && (firstMockup.url || firstMockup.mockupUrl || firstMockup.mockup_url || firstMockup.previewUrl)) ||
        data.mockupUrl || data.previewUrl || null;
      var variantIds = data.variantIds || data.variant_ids || template.variantIds || template.variant_ids || [];
      var templateId = pickProp(template, ["templateId", "template_id", "id"]) || pickProp(data, ["templateId", "template_id"]);
      return {
        status: "saved",
        externalProductId: pickProp(data, ["externalProductId", "external_product_id"]) ||
          (studio.session && studio.session.externalProductId) || null,
        templateId: typeof templateId === "number" ? templateId : (templateId != null ? num(templateId, null) : null),
        templateExternalId: pickProp(template, ["externalId", "external_id", "templateExternalId"]) ||
          pickProp(data, ["externalId", "external_id"]),
        variantIds: Array.isArray(variantIds) ? variantIds.map(function (v) { return num(v, null); }).filter(Boolean) : [],
        mockupUrl: (typeof mockupUrl === "string" && /^https:/.test(mockupUrl)) ? mockupUrl : null,
        designValid: data.designValid != null ? data.designValid : (studio.designStatus ? studio.designStatus.designValid : null)
      };
    }

    // Native design state reported by onDesignStatusUpdate (variants/placements
    // are the SDK's source of truth even before an explicit save).
    function normalizePlacementId(value) {
      return String(value == null ? "" : value).trim();
    }

    function knownPlacementIds(product) {
      var ids = {};
      (product && product.placements || []).forEach(function (placement) {
        if (placement && placement.id) ids[String(placement.id)] = true;
      });
      if (product && product.defaultPlacementId) ids[String(product.defaultPlacementId)] = true;
      return ids;
    }

    function placementLabel(product, placementId) {
      var id = normalizePlacementId(placementId);
      var match = (product && product.placements || []).filter(function (placement) { return placement && placement.id === id; })[0];
      if (match && match.label) return match.label;
      return id.replace(/^embroidery_/, "").replace(/_/g, " ").replace(/\b\w/g, function (letter) { return letter.toUpperCase(); });
    }

    function isFrontIncludedPlacement(id) {
      var value = String(id || "").toLowerCase();
      return value.indexOf("front") !== -1 || value.indexOf("chest_left") !== -1 || value.indexOf("apparel_front") !== -1;
    }

    function includedPlacementForProduct(product) {
      var placements = product && product.placements || [];
      var preferred = product && product.defaultPlacementId;
      var frontLarge = placements.filter(function (placement) {
        return placement && placement.id && String(placement.id).toLowerCase().indexOf("front_large") !== -1;
      })[0];
      var front = placements.filter(function (placement) {
        return placement && placement.id && isFrontIncludedPlacement(placement.id);
      })[0];
      var selected = placements.filter(function (placement) { return placement && placement.id === preferred; })[0] || frontLarge || front || placements[0] || null;
      return {
        id: selected ? selected.id : (preferred || null),
        label: selected ? selected.label : (preferred ? placementLabel(product, preferred) : null)
      };
    }

    function isIncludedPlacement(placementId, includedId) {
      var id = normalizePlacementId(placementId);
      var included = normalizePlacementId(includedId);
      if (!id || !included) return false;
      if (id === included) return true;
      return isFrontIncludedPlacement(id) && isFrontIncludedPlacement(included);
    }

    function placementRecordLooksUsed(record) {
      if (!record || typeof record !== "object") return false;
      var trueKeys = ["isUsed", "used", "active", "selected", "hasDesign", "has_design", "hasLayers", "configured"];
      for (var i = 0; i < trueKeys.length; i += 1) {
        if (record[trueKeys[i]] === true) return true;
      }
      var arrayKeys = ["layers", "files", "images", "objects", "printFiles", "print_files"];
      for (var j = 0; j < arrayKeys.length; j += 1) {
        if (Array.isArray(record[arrayKeys[j]]) && record[arrayKeys[j]].length > 0) return true;
      }
      var status = String(record.status || record.state || "").toLowerCase();
      return status === "used" || status === "active" || status === "configured";
    }

    function placementIdFromRecord(record) {
      return pickProp(record, ["placement", "placementId", "placement_id", "id", "key", "name"]);
    }

    function addUsedPlacement(out, seen, product, value) {
      var id = normalizePlacementId(value);
      if (!id || id === "mockup") return;
      var known = knownPlacementIds(product);
      if (Object.keys(known).length && !known[id] && !isFrontIncludedPlacement(id) && id.indexOf("back") === -1 && id.indexOf("sleeve") === -1) return;
      if (!seen[id]) {
        seen[id] = true;
        out.push(id);
      }
    }

    function collectPlacementValues(value, product, out, seen, requireUsage) {
      if (!value) return;
      if (typeof value === "string" || typeof value === "number") {
        if (!requireUsage) addUsedPlacement(out, seen, product, value);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(function (entry) { collectPlacementValues(entry, product, out, seen, requireUsage); });
        return;
      }
      if (typeof value === "object") {
        if (!requireUsage || placementRecordLooksUsed(value)) addUsedPlacement(out, seen, product, placementIdFromRecord(value));
      }
    }

    function collectUsedPlacements(status, product) {
      var out = [];
      var seen = {};
      var foundSignal = false;
      var explicitKeys = [
        "usedPlacements", "used_placements", "selectedPlacements", "selected_placements",
        "activePlacements", "active_placements", "configuredPlacements", "configured_placements",
        "designPlacements", "design_placements", "printPlacements", "print_placements",
        "placementsWithDesigns", "placements_with_designs"
      ];
      explicitKeys.forEach(function (key) {
        if (status && status[key] != null) {
          foundSignal = true;
          collectPlacementValues(status[key], product, out, seen, false);
        }
      });
      ["design", "template", "savedProduct", "product"].forEach(function (key) {
        var nested = status && status[key];
        if (nested && typeof nested === "object") {
          explicitKeys.forEach(function (nestedKey) {
            if (nested[nestedKey] != null) {
              foundSignal = true;
              collectPlacementValues(nested[nestedKey], product, out, seen, false);
            }
          });
        }
      });
      if (status && status.placements != null) {
        foundSignal = true;
        collectPlacementValues(status.placements, product, out, seen, true);
      }
      return { placements: out, foundSignal: foundSignal };
    }

    function nativeStatusMeta(studio, product) {
      var s = studio && studio.designStatus;
      if (!s) {
        return {
          variantIds: [],
          designChanged: false,
          designValid: null,
          usedPlacements: [],
          placementPricingStatus: studio && studio.ready ? "pending" : "not_applicable"
        };
      }
      var placementMeta = collectUsedPlacements(s, product);
      var selectedVariantIds = s.selectedVariantIds || s.selected_variant_ids || s.variantIds || s.variant_ids || [];
      return {
        variantIds: Array.isArray(selectedVariantIds) ? selectedVariantIds.map(function (v) { return num(v, null); }).filter(Boolean) : [],
        designChanged: s.designChange === true,
        designValid: s.designValid === true,
        usedPlacements: placementMeta.placements,
        placementPricingStatus: placementMeta.foundSignal ? "ready" : "unavailable"
      };
    }

    function modalPlacementPricing(m) {
      var product = m && m.product;
      var included = includedPlacementForProduct(product);
      var statusMeta = nativeStatusMeta(state.studio, product);
      var usedPlacements = statusMeta.usedPlacements || [];
      var placementPricingStatus = statusMeta.placementPricingStatus || "not_applicable";
      if (!state.studio) {
        usedPlacements = [m && m.placementId || included.id].filter(Boolean);
        placementPricingStatus = "not_applicable";
      }
      var extraPlacements = usedPlacements.filter(function (placementId) {
        return !isIncludedPlacement(placementId, included.id);
      });
      var extraPrintCount = placementPricingStatus === "ready" || placementPricingStatus === "not_applicable" ? extraPlacements.length : 0;
      var extraUnitTotalCents = extraPrintCount * EXTRA_PRINT_UNIT_CENTS;
      return {
        includedPlacement: included.id,
        includedPlacementLabel: included.label,
        usedPlacements: usedPlacements,
        extraPlacements: extraPlacements,
        extraPrintCount: extraPrintCount,
        extraPrintUnitPriceCents: EXTRA_PRINT_UNIT_CENTS,
        extraPrintUnitTotalCents: extraUnitTotalCents,
        placementPricingStatus: placementPricingStatus
      };
    }

    function extraPrintPricingForQuantity(pricing, qty) {
      if (!pricing) return null;
      return {
        includedPlacement: pricing.includedPlacement || null,
        includedPlacementLabel: pricing.includedPlacementLabel || null,
        usedPlacements: pricing.usedPlacements || [],
        extraPlacements: pricing.extraPlacements || [],
        extraPrintCount: pricing.extraPrintCount || 0,
        extraPrintUnitPriceCents: pricing.extraPrintUnitPriceCents || EXTRA_PRINT_UNIT_CENTS,
        extraPrintUnitTotalCents: pricing.extraPrintUnitTotalCents || 0,
        extraPrintSubtotalCents: (pricing.extraPrintUnitTotalCents || 0) * Math.max(0, num(qty, 0)),
        placementPricingStatus: pricing.placementPricingStatus || "not_applicable"
      };
    }

    // Saves the native design (if a ready studio is open) and returns its
    // template metadata, or a save_failed marker, or null when not applicable.
    function captureNativeDesign() {
      var studio = state.studio;
      if (!studio || !studio.ready || !studio.instance || !studio.instance.saveDesign) {
        return Promise.resolve(null);
      }
      var meta = nativeStatusMeta(studio, state.modal && state.modal.product);
      return Promise.resolve(studio.instance.saveDesign())
        .then(function (res) {
          if (res && res.error) {
            return {
              status: "save_failed", error: String((res.error.message) || res.error).slice(0, 200),
              variantIds: meta.variantIds || [],
              designChanged: meta.designChanged, designValid: meta.designValid,
              usedPlacements: meta.usedPlacements || [],
              placementPricingStatus: meta.placementPricingStatus
            };
          }
          var data = (res && res.data) || (res && !("data" in res) ? res : null);
          if (!data || typeof data !== "object") {
            return {
              status: "opened", variantIds: meta.variantIds || [],
              designChanged: meta.designChanged, designValid: meta.designValid,
              usedPlacements: meta.usedPlacements || [],
              placementPricingStatus: meta.placementPricingStatus
            };
          }
          var native = normalizeNativeDesign(data, studio);
          if (!native.variantIds.length && meta.variantIds && meta.variantIds.length) native.variantIds = meta.variantIds;
          native.designChanged = meta.designChanged;
          native.usedPlacements = meta.usedPlacements || [];
          native.placementPricingStatus = meta.placementPricingStatus;
          return native;
        })
        .catch(function (err) {
          return {
            status: "save_failed", error: String((err && err.message) || err).slice(0, 200),
            variantIds: meta.variantIds || [],
            designChanged: meta.designChanged, designValid: meta.designValid,
            usedPlacements: meta.usedPlacements || [],
            placementPricingStatus: meta.placementPricingStatus
          };
        });
    }

    // ---- design modal ----------------------------------------------------
    function openModal(handle) {
      var product = state.byHandle[handle];
      if (!product || product.catalogLoadState !== "ready") return;
      fireStartOnce();
      var defaultColor = 0;
      if (product.defaultColorId) {
        for (var i = 0; i < (product.colors || []).length; i += 1) {
          if (product.colors[i].id === product.defaultColorId) { defaultColor = i; break; }
        }
      }
      state.modal = {
        handle: handle,
        product: product,
        colorIndex: defaultColor,
        placementId: product.defaultPlacementId || (product.placements && product.placements[0] && product.placements[0].id) || null,
        qty: {},
        viewUrls: [],
        viewColor: null,
        activeView: 0
      };
      renderModal();
      showModal();
      mountStudio(state.modal);
      fire("bulk_product_editor_open", { item_id: handle });
    }

    function renderModal() {
      var m = state.modal;
      if (!m) return;
      var product = m.product;
      if (els.modalCat) els.modalCat.textContent = product.categoryName || "";
      if (els.modalTitle) els.modalTitle.textContent = product.title || "";
      if (els.modalPrice) {
        els.modalPrice.innerHTML = escapeHtml(product.displayPriceLabel || ("Est. " + money(product.displayUnitPriceCents))) +
          ' <small>per item, before bulk pricing</small>';
      }

      // Colors
      var colors = product.colors || [];
      if (els.colorsWrap) els.colorsWrap.hidden = state.studio ? true : colors.length < 2;
      if (els.swatches) {
        els.swatches.innerHTML = colors.map(function (color, index) {
          var hex = color.hex || color.mockupBackgroundColor || "#cccccc";
          var active = index === m.colorIndex ? " is-active" : "";
          var light = isLightHex(hex) ? ' data-light="1"' : "";
          return '<button class="mfc__swatch' + active + '" type="button" title="' + escapeAttr(color.color || "Color") +
            '" data-mfc-swatch="' + index + '"' + light + ' style="background-color:' + escapeAttr(hex) + '"></button>';
        }).join("");
      }
      if (els.colorName) els.colorName.textContent = colors[m.colorIndex] ? colors[m.colorIndex].color : "";

      // Placements (hidden while the native studio owns placement selection).
      var placements = product.placements || [];
      if (els.placementWrap) els.placementWrap.hidden = state.studio ? true : placements.length < 2;
      if (els.placements) {
        els.placements.innerHTML = placements.map(function (placement) {
          var active = placement.id === m.placementId ? " is-active" : "";
          return '<button class="mfc__chip' + active + '" type="button" data-mfc-placement="' + escapeAttr(placement.id) + '">' +
            escapeHtml(placement.label) + '</button>';
        }).join("");
      }

      renderSizes();
      updateModalRunning();
      if (els.modalError) { els.modalError.hidden = true; els.modalError.textContent = ""; }
    }

    function renderSizes() {
      var m = state.modal;
      if (!m || !els.sizes) return;
      var color = (m.product.colors || [])[m.colorIndex];
      var sizes = (color && color.sizes) || [];
      if (!sizes.length) {
        els.sizes.innerHTML = '<div class="mfc__size"><label>Qty</label>' +
          '<input type="number" inputmode="numeric" min="0" step="1" value="' + (m.qty["One size"] || "") +
          '" data-mfc-qty="One size" data-variant="' + escapeAttr(color ? color.printfulCatalogVariantId : "") + '"></div>';
        return;
      }
      els.sizes.innerHTML = sizes.map(function (sizeOption) {
        var value = m.qty[sizeOption.size] || "";
        return '<div class="mfc__size"><label>' + escapeHtml(sizeOption.size) + '</label>' +
          '<input type="number" inputmode="numeric" min="0" step="1" value="' + value +
          '" data-mfc-qty="' + escapeAttr(sizeOption.size) +
          '" data-variant="' + escapeAttr(sizeOption.printfulCatalogVariantId) + '"></div>';
      }).join("");
    }

    function modalTotals() {
      var m = state.modal;
      var qty = 0;
      Object.keys(m.qty).forEach(function (size) { qty += Math.max(0, num(m.qty[size], 0)); });
      var pricing = modalPlacementPricing(m);
      var baseUnitCents = num(m.product.displayUnitPriceCents, 0);
      var unitCents = baseUnitCents + pricing.extraPrintUnitTotalCents;
      var cents = unitCents * qty;
      return { qty: qty, cents: cents, baseUnitCents: baseUnitCents, unitCents: unitCents, pricing: pricing };
    }

    function placementStatusBlocksAdd(status) {
      return status === "pending" || status === "unavailable";
    }

    function renderExtraPrintNote(totals) {
      if (!els.extraPrintNote || !state.modal) return;
      var pricing = totals.pricing || {};
      els.extraPrintNote.classList.remove("is-active");
      els.extraPrintNote.classList.remove("is-warning");
      if (pricing.placementPricingStatus === "pending") {
        els.extraPrintNote.textContent = "Waiting for the Design Studio to report print areas before pricing.";
        els.extraPrintNote.classList.add("is-warning");
        return;
      }
      if (pricing.placementPricingStatus === "unavailable") {
        els.extraPrintNote.textContent = "The Design Studio did not report print areas. We need that before showing an estimate.";
        els.extraPrintNote.classList.add("is-warning");
        return;
      }
      if (pricing.extraPrintCount > 0) {
        var labels = pricing.extraPlacements.map(function (id) { return placementLabel(state.modal.product, id); }).join(", ");
        els.extraPrintNote.textContent = labels + ": +" + money(pricing.extraPrintUnitTotalCents) + "/item for extra print areas.";
        els.extraPrintNote.classList.add("is-active");
        return;
      }
      els.extraPrintNote.textContent = "Includes one front print. Additional print areas add " + money(EXTRA_PRINT_UNIT_CENTS) + "/item each.";
    }

    function updateModalRunning() {
      var totals = modalTotals();
      if (els.modalQty) els.modalQty.textContent = totals.qty;
      if (els.modalLine) els.modalLine.textContent = money(totals.cents);
      renderExtraPrintNote(totals);
      if (els.modalAdd && state.modal && !state.modal.saving) {
        var blocked = placementStatusBlocksAdd(totals.pricing.placementPricingStatus);
        els.modalAdd.disabled = blocked;
        els.modalAdd.title = blocked ? "Waiting for Design Studio print-area pricing." : "";
      }
    }

    function modalPreviewCurrent(m, colorIndex, placementId) {
      return state.modal === m && m.colorIndex === colorIndex && m.placementId === placementId;
    }

    function nativeMockupViewsFromResult(result, arg) {
      if (result && result.error) throw new Error(reasonText(result.error));
      var data = (result && result.data) || result || {};
      var mockups = Array.isArray(data.mockups) ? data.mockups : [];
      var exact = [];
      var loose = [];
      mockups.forEach(function (mockup) {
        if (!mockup || !mockup.blob || mockup.failed) return;
        var productId = positiveNumber(mockup.productId);
        if (productId && productId !== arg.productId) return;
        var variantId = nativeMockupResultVariantId(mockup);
        var target = variantId ? (variantId === arg.selectedVariantId ? exact : null) : loose;
        if (!target) return;
        target.push({
          blob: mockup.blob,
          label: mockup.label || mockup.title || mockup.view || mockup.placement || null,
          score: nativeMockupStyleScore(mockup, arg)
        });
      });
      return (exact.length ? exact : loose).sort(function (a, b) { return a.score - b.score; }).slice(0, MAX_PREVIEWS).map(function (mockup, index) {
        return {
          url: URL.createObjectURL(mockup.blob),
          label: mockup.label || ("Printful preview " + (index + 1)),
          owned: true
        };
      });
    }

    function showModalProductImage(m, colorIndex, placementId, note) {
      if (!modalPreviewCurrent(m, colorIndex, placementId)) return;
      if (els.stage) els.stage.classList.remove("is-loading");
      var imageUrl = productImage(m.product, colorIndex);
      clearProductFill(els.stage);
      if (els.stageImg) els.stageImg.src = imageUrl;
      getProductPreviewUrl(m.product, colorIndex).then(function (url) {
        if (!modalPreviewCurrent(m, colorIndex, placementId)) return;
        if (els.stageImg && url) els.stageImg.src = url;
      });
      if (els.views) els.views.innerHTML = "";
      if (els.previewNote) els.previewNote.textContent = note || "";
    }

    function showModalPreviewUnavailable(m, colorIndex, placementId, reason) {
      if (!modalPreviewCurrent(m, colorIndex, placementId)) return;
      revokeViewUrls();
      showModalProductImage(
        m,
        colorIndex,
        placementId,
        "Exact Printful preview is unavailable for this product. We are not showing an approximate logo mockup."
      );
      notify("Exact Printful preview is unavailable for this product. We are not showing an approximate logo mockup.");
      if (window.console && window.console.warn) {
        window.console.warn("[Makefield bulk] Exact Printful modal preview unavailable", {
          handle: m.handle,
          reason: reasonText(reason)
        });
      }
    }

    function loadModalPreview() {
      var m = state.modal;
      if (!m) return;
      var product = m.product;
      var colorIndex = m.colorIndex;
      var placementId = m.placementId;
      // No logo: show the selected color's product image.
      if (!state.logo) {
        revokeViewUrls();
        showModalProductImage(m, colorIndex, placementId, "Add a logo above to preview it on this product. You can still pick colors, sizes, and quantities now.");
        return;
      }
      // Logo present: generate logo-applied views via Printful/FYUL's native mockup engine.
      var productArg = buildNativeMockupProductArg(product, colorIndex);
      if (!productArg) {
        showModalPreviewUnavailable(m, colorIndex, placementId, "Missing Printful product or variant mapping.");
        return;
      }
      if (els.previewNote) els.previewNote.textContent = "Building your logo preview\u2026";
      if (els.stage) els.stage.classList.add("is-loading");
      Promise.all([ensureSdk(), getLogoFile()])
        .then(function (parts) {
          if (!modalPreviewCurrent(m, colorIndex, placementId)) return null;
          return Promise.resolve(parts[0].mockup.getMockups({ file: parts[1], products: [productArg] }));
        })
        .then(function (result) {
          if (!result || !modalPreviewCurrent(m, colorIndex, placementId)) return;
          var views = nativeMockupViewsFromResult(result, productArg);
          if (!views.length) throw new Error("Printful did not return a usable exact mockup.");
          revokeViewUrls();
          m.viewUrls = views;
          m.viewColor = ((product.colors || [])[colorIndex] || {}).color || null;
          m.activeView = 0;
          renderViews();
          if (els.previewNote) els.previewNote.textContent = "Exact Printful preview generated for this product.";
        })
        .catch(function (err) { showModalPreviewUnavailable(m, colorIndex, placementId, err); });
    }

    function revokeViewUrls() {
      var m = state.modal;
      if (!m || !m.viewUrls) return;
      m.viewUrls.forEach(function (v) {
        if (v && v.owned !== false && v.url && v.url.indexOf("blob:") === 0) {
          try { URL.revokeObjectURL(v.url); } catch (e) {}
        }
      });
      m.viewUrls = [];
    }

    function renderViews() {
      var m = state.modal;
      if (!m) return;
      if (els.stage) els.stage.classList.remove("is-loading");
      clearProductFill(els.stage);
      if (els.stageImg && m.viewUrls[m.activeView]) els.stageImg.src = m.viewUrls[m.activeView].url;
      if (!els.views) return;
      if (m.viewUrls.length < 2) { els.views.innerHTML = ""; return; }
      els.views.innerHTML = m.viewUrls.map(function (v, index) {
        var active = index === m.activeView ? " is-active" : "";
        return '<button class="mfc__view' + active + '" type="button" title="' + escapeAttr(v.label || ("View " + (index + 1))) +
          '" data-mfc-view="' + index + '" style="background-image:url(\'' + v.url + '\')"></button>';
      }).join("");
    }

    function showModal() {
      if (!els.modal) return;
      state.lastFocus = document.activeElement;
      els.modal.hidden = false;
      els.modal.classList.add("is-open");
      document.body.style.overflow = "hidden";
      window.setTimeout(function () { if (els.modalClose) els.modalClose.focus(); }, 0);
    }

    function closeModal() {
      if (!els.modal) return;
      destroyStudio();
      revokeViewUrls();
      els.modal.classList.remove("is-open");
      els.modal.hidden = true;
      document.body.style.overflow = "";
      state.modal = null;
      if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus();
    }

    function trapFocus(event) {
      if (event.key !== "Tab" || !els.modal || els.modal.hidden) return;
      var focusable = $all(els.modal, 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        .filter(function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    function capturePreviewDataUrl() {
      var m = state.modal;
      if (!m || !m.viewUrls || !m.viewUrls.length) return Promise.resolve(null);
      var src = m.viewUrls[0].url;
      return new Promise(function (resolve) {
        var img = new Image();
        img.onload = function () {
          try {
            var maxDim = 640;
            var scale = Math.min(1, maxDim / Math.max(img.width || maxDim, img.height || maxDim));
            var w = Math.max(1, Math.round((img.width || maxDim) * scale));
            var h = Math.max(1, Math.round((img.height || maxDim) * scale));
            var canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            var ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            var quality = 0.72;
            var dataUrl = canvas.toDataURL("image/jpeg", quality);
            while (dataUrl.length > MAX_PREVIEW_DATAURL && quality > 0.4) {
              quality -= 0.12;
              dataUrl = canvas.toDataURL("image/jpeg", quality);
            }
            resolve(dataUrl.length <= MAX_PREVIEW_DATAURL ? dataUrl : null);
          } catch (e) { resolve(null); }
        };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    }

    function addDesign() {
      var m = state.modal;
      if (!m) return;
      var totals = modalTotals();
      if (!totals.qty) {
        if (els.modalError) { els.modalError.hidden = false; els.modalError.textContent = "Add a quantity for at least one size."; }
        return;
      }
      if (placementStatusBlocksAdd(totals.pricing.placementPricingStatus)) {
        if (els.modalError) {
          els.modalError.hidden = false;
          els.modalError.textContent = "The Design Studio needs to report print areas before we can estimate this item.";
        }
        updateModalRunning();
        return;
      }
      var product = m.product;
      var color = (product.colors || [])[m.colorIndex] || null;
      var placement = (product.placements || []).filter(function (p) { return p.id === m.placementId; })[0]
        || (product.placements || [])[0] || null;
      var studioReady = Boolean(state.studio && state.studio.ready);

      m.saving = true;
      if (els.modalAdd) { els.modalAdd.disabled = true; els.modalAdd.textContent = studioReady ? "Saving design\u2026" : "Adding\u2026"; }

      Promise.all([capturePreviewDataUrl(), captureNativeDesign()]).then(function (parts) {
        var previewDataUrl = parts[0];
        var native = parts[1];
        var finalTotals = modalTotals();
        var finalPricing = finalTotals.pricing;
        if (placementStatusBlocksAdd(finalPricing.placementPricingStatus)) {
          if (els.modalError) {
            els.modalError.hidden = false;
            els.modalError.textContent = "The Design Studio needs to report print areas before we can estimate this item.";
          }
          throw new Error("Design Studio print-area status unavailable");
        }

        var groupStatus = "native_preview_only";
        if (native && native.status === "saved") groupStatus = "saved";
        else if (native && native.status === "save_failed") groupStatus = "save_failed";
        else if (native && native.status === "opened") groupStatus = "opened";
        else if (studioReady) groupStatus = "opened";

        var sizes = [];
        var color2 = (product.colors || [])[m.colorIndex] || null;
        var sizeVariantMap = {};
        ((color2 && color2.sizes) || []).forEach(function (s) { sizeVariantMap[s.size] = s.printfulCatalogVariantId; });
        Object.keys(m.qty).forEach(function (size) {
          var qty = Math.max(0, num(m.qty[size], 0));
          if (qty > 0) sizes.push({ size: size, qty: qty, variantId: sizeVariantMap[size] || (color2 ? color2.printfulCatalogVariantId : null) });
        });

        state.designSeq += 1;
        var design = {
          id: "mfc-design-" + state.designSeq,
          handle: product.handle,
          productTitle: product.title,
          category: product.categoryName,
          categoryId: product.categoryId,
          finishingType: product.finishingType,
          printfulCatalogProductId: product.printfulCatalogProductId,
          baseUnitPriceCents: product.displayUnitPriceCents,
          unitPriceCents: finalTotals.unitCents,
          priceLabel: product.displayPriceLabel,
          color: color ? color.color : null,
          placementId: placement ? placement.id : null,
          placementLabel: placement ? placement.label : null,
          technique: placement ? placement.technique : product.finishingType,
          techniqueLabel: placement ? placement.techniqueLabel : null,
          // Native studio metadata (source of truth for the saved artwork).
          studioType: state.studio ? state.studio.type : null,
          groupStatus: groupStatus,
          externalProductId: (native && native.externalProductId) || (state.studio && state.studio.session && state.studio.session.externalProductId) || null,
          printfulTemplateId: (native && native.templateId) || null,
          printfulTemplateExternalId: (native && native.templateExternalId) || null,
          nativeVariantIds: (native && native.variantIds) || [],
          usedPlacements: finalPricing.usedPlacements || [],
          extraPlacements: finalPricing.extraPlacements || [],
          extraPrintPricing: extraPrintPricingForQuantity(finalPricing, finalTotals.qty),
          mockupUrl: (native && native.mockupUrl) || null,
          designChanged: native ? native.designChanged : null,
          designValid: native ? (native.designValid !== false) : null,
          designError: (native && native.error) || null,
          previewDataUrl: previewDataUrl,
          thumbUrl: (native && native.mockupUrl) || (m.viewUrls && m.viewUrls[0] && m.viewUrls[0].url) || (els.stageImg && els.stageImg.src) || productImage(product, m.colorIndex),
          sizes: sizes
        };
        state.designs.push(design);
        renderSummary();
        fire("bulk_quote_product_add", {
          item_id: product.handle, item_name: product.title, quantity: totals.qty,
          design_source: groupStatus
        });
        if (els.modalAdd) { els.modalAdd.disabled = false; els.modalAdd.textContent = "Add to request"; }
        m.saving = false;
        closeModal();
      }).then(null, function () {
        m.saving = false;
        if (els.modalAdd) { els.modalAdd.disabled = false; els.modalAdd.textContent = "Add to request"; }
        updateModalRunning();
      });
    }

    // ---- summary ---------------------------------------------------------
    function designQty(design) {
      return design.sizes.reduce(function (sum, s) { return sum + s.qty; }, 0);
    }

    function renderSummary() {
      var totalItems = 0;
      var totalCents = 0;
      state.designs.forEach(function (design) {
        var qty = designQty(design);
        totalItems += qty;
        totalCents += design.unitPriceCents * qty;
      });
      if (els.totalItems) els.totalItems.textContent = totalItems;
      if (els.subtotal) els.subtotal.textContent = money(totalCents);
      if (els.tier) els.tier.textContent = quoteTierText(totalItems);

      if (els.designs) {
        els.designs.innerHTML = state.designs.map(function (design) {
          var qty = designQty(design);
          var extra = design.extraPrintPricing && design.extraPrintPricing.extraPrintCount
            ? design.extraPrintPricing.extraPrintCount + (design.extraPrintPricing.extraPrintCount === 1 ? " extra print" : " extra prints")
            : null;
          var meta = [design.color, qty + (qty === 1 ? " item" : " items"), extra].filter(Boolean).join(" \u00b7 ");
          var thumbStyle = design.thumbUrl ? ' style="background-image:url(\'' + escapeAttr(design.thumbUrl) + '\')"' : "";
          return '<div class="mfc__design-row" data-mfc-design-row="' + escapeAttr(design.id) + '">' +
            '<span class="mfc__design-thumb"' + thumbStyle + '></span>' +
            '<span class="mfc__design-info">' +
              '<span class="mfc__design-title">' + escapeHtml(design.productTitle || "") + '</span>' +
              '<span class="mfc__design-meta">' + escapeHtml(meta) + '</span>' +
            '</span>' +
            '<button class="mfc__design-remove" type="button" aria-label="Remove" data-mfc-design-remove="' + escapeAttr(design.id) + '">&times;</button>' +
            '</div>';
        }).join("");
      }
    }

    function removeDesign(id) {
      var design = state.designs.filter(function (d) { return d.id === id; })[0];
      state.designs = state.designs.filter(function (d) { return d.id !== id; });
      renderSummary();
      if (design) fire("bulk_quote_product_remove", { item_id: design.handle, item_name: design.productTitle });
    }

    // ---- submit ----------------------------------------------------------
    function buildPayload() {
      var lineItems = [];
      var designGroups = [];
      var designPreviews = [];

      state.designs.forEach(function (design) {
        var sizeLabels = [];
        var variantIds = [];
        design.sizes.forEach(function (s) {
          sizeLabels.push(s.size);
          if (s.variantId) variantIds.push(Number(s.variantId));
          lineItems.push({
            kind: "standard_product",
            productHandle: design.handle,
            variantId: s.variantId ? String(s.variantId) : null,
            printfulCatalogVariantId: s.variantId ? Number(s.variantId) : null,
            productTitle: design.productTitle,
            category: design.category,
            finishingType: design.finishingType,
            selectedColor: design.color,
            selectedSize: s.size,
            selectedPlacement: design.placementId,
            selectedPlacementLabel: design.placementLabel,
            decorationTechnique: design.technique,
	          proofRequired: true,
	          baseUnitPriceCents: design.baseUnitPriceCents || design.unitPriceCents || null,
	          displayUnitPriceCents: design.unitPriceCents || null,
	          displayPriceLabel: design.priceLabel,
	          extraPrintPricing: extraPrintPricingForQuantity(design.extraPrintPricing, s.qty),
	          approxQuantity: s.qty,
	          notes: null
          });
        });

        var mergedVariantIds = variantIds.slice();
        (design.nativeVariantIds || []).forEach(function (id) {
          var n = Number(id);
          if (n && mergedVariantIds.indexOf(n) === -1) mergedVariantIds.push(n);
        });

        designGroups.push({
          groupId: design.id,
          productHandle: design.handle,
          productTitle: design.productTitle,
          printfulCatalogProductId: design.printfulCatalogProductId || null,
          selectedPlacement: design.placementId,
          selectedPlacementLabel: design.placementLabel,
          technique: design.technique,
          techniqueLabel: design.techniqueLabel,
          externalProductId: design.externalProductId || null,
          printfulTemplateId: design.printfulTemplateId || null,
          printfulTemplateExternalId: design.printfulTemplateExternalId || null,
          selectedVariantIds: mergedVariantIds,
	          selectedColors: design.color ? [design.color] : [],
	          selectedSizes: sizeLabels,
	          baseUnitPriceCents: design.baseUnitPriceCents || design.unitPriceCents || null,
	          displayUnitPriceCents: design.unitPriceCents || null,
	          extraPrintPricing: extraPrintPricingForQuantity(design.extraPrintPricing, designQty(design)),
	          designValid: design.designValid != null ? design.designValid : true,
          designChanged: design.designChanged != null ? design.designChanged : true,
          status: design.groupStatus || "saved",
          mockupUrl: design.mockupUrl || null,
          savedAt: new Date().toISOString(),
          error: design.designError || null
        });

        if (design.previewDataUrl && designPreviews.length < MAX_PREVIEWS) {
          designPreviews.push({
            productHandle: design.handle,
            productTitle: design.productTitle,
            selectedColor: design.color,
            selectedPlacement: design.placementId,
            selectedPlacementLabel: design.placementLabel,
            decorationTechnique: design.technique,
            quantity: designQty(design),
            previewDataUrl: design.previewDataUrl,
            previewStatus: "captured"
          });
        }
      });

      var total = lineItems.reduce(function (sum, item) { return sum + num(item.approxQuantity, 0); }, 0);
      var subtotal = lineItems.reduce(function (sum, item) {
        return sum + num(item.displayUnitPriceCents, 0) * num(item.approxQuantity, 0);
      }, 0);

      var logoAsset = null;
      if (state.logo) {
        logoAsset = {
          assetId: state.logo.assetId || null,
          source: state.logo.source === "store_graphic" ? "store_graphic" : (state.logo.source === "uploaded" ? "uploaded" : "browser_preview"),
          fileName: state.logo.fileName || null,
          contentType: state.logo.contentType || null,
          publicImageUrl: state.logo.publicImageUrl || null,
          sha256: state.logo.sha256 || null
        };
      }

      return {
        honeypot: els.honeypot ? els.honeypot.value : "",
        contact: {
          name: val(els.name),
          email: val(els.email),
          phone: val(els.phone),
          role: val(els.role)
        },
        organization: {
          name: val(els.org),
          existingStoreUrl: val(els.storeUrl),
          collectionHandle: state.storeGraphic ? state.storeGraphic.collectionHandle : (params.get("collection_handle") || null),
          organizationId: state.storeGraphic ? state.storeGraphic.organizationId : (params.get("organization_id") || null)
        },
        request: {
          deadline: val(els.deadline),
          eventName: val(els.event),
          conversionPath: "concierge_quote",
          lineItems: lineItems,
          approxTotalQuantity: total,
          baselineSubtotalCents: subtotal || null,
          logo: {
            fileName: state.logo ? state.logo.fileName : null,
            contentType: state.logo ? state.logo.contentType : null,
            visualized: Boolean(state.logo),
            scalePercent: null,
            offsetXPercent: null,
            offsetYPercent: null,
            previewDataUrl: null,
            previewDataUrlStatus: "not_available"
          },
          logoAsset: logoAsset,
          storeGraphic: state.storeGraphic,
          designGroups: designGroups,
          designPreviews: designPreviews,
          checkout: {
            mode: "manual_review",
            readiness: lineItems.length ? "ready_for_staff_quote" : "needs_review",
            customerCheckoutIntent: false,
            shippingAddress: null
          },
          customItemsNote: val(els.customNote),
          budgetNote: val(els.notes),
          rush: false
        },
        source: {
          surface: sourceSurface,
          url: window.location.href.slice(0, 500),
          referrer: (document.referrer || "").slice(0, 500) || null,
          utmSource: params.get("utm_source") || null,
          utmMedium: params.get("utm_medium") || null,
          utmCampaign: params.get("utm_campaign") || null,
          mfCampaignId: params.get("mf_campaign_id") || null,
          mfActionId: params.get("mf_action_id") || null,
          mfLinkId: params.get("mf_link_id") || null
        }
      };
    }

    function val(el) { return el ? el.value : null; }

    function submitForm(event) {
      event.preventDefault();
      if (els.error) { els.error.hidden = true; els.error.textContent = ""; }
      var name = val(els.name) && val(els.name).trim();
      var email = val(els.email) && val(els.email).trim();
      var org = val(els.org) && val(els.org).trim();
      if (!name || !email || !org) {
        showFormError("Please add your name, email, and team / school / company.");
        return;
      }
      if (!state.designs.length && !(val(els.customNote) && val(els.customNote).trim())) {
        showFormError("Add at least one product, or tell us what you need under \u201cWant something not shown here?\u201d");
        return;
      }
      if (els.submit) { els.submit.disabled = true; els.submit.textContent = "Sending\u2026"; }
      fire("bulk_quote_submit");
      postJson(endpoints.request, buildPayload())
        .then(function (body) {
          if (els.form) els.form.hidden = true;
          if (els.confirm) { els.confirm.hidden = false; els.confirm.focus(); }
          fire("bulk_quote_submit_success", { request_id: body && body.requestId });
        })
        .catch(function (err) {
          showFormError("Something went wrong sending your request. Please try again, or email john@makefield.shop.");
          if (els.submit) { els.submit.disabled = false; els.submit.textContent = "Send request"; }
          fire("bulk_quote_submit_error", { error_message: String(err && err.message || err).slice(0, 120) });
        });
    }

    function showFormError(message) {
      if (!els.error) return;
      els.error.hidden = false;
      els.error.textContent = message;
      els.error.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // ---- events ----------------------------------------------------------
    function bindEvents() {
      if (els.logoButton) els.logoButton.addEventListener("click", function () { if (els.logoInput) els.logoInput.click(); });
      if (els.logoInput) els.logoInput.addEventListener("change", function (e) {
        var file = e.target.files && e.target.files[0];
        if (file) handleLogoFile(file);
      });
      if (els.logoClear) els.logoClear.addEventListener("click", clearLogo);

      if (els.logoDrop) {
        ["dragenter", "dragover"].forEach(function (type) {
          els.logoDrop.addEventListener(type, function (e) { e.preventDefault(); els.logoDrop.classList.add("is-dragover"); });
        });
        ["dragleave", "drop"].forEach(function (type) {
          els.logoDrop.addEventListener(type, function (e) { e.preventDefault(); els.logoDrop.classList.remove("is-dragover"); });
        });
        els.logoDrop.addEventListener("drop", function (e) {
          var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (file) handleLogoFile(file);
        });
      }

      if (els.storeSearchButton) els.storeSearchButton.addEventListener("click", searchStores);
      if (els.storeSearch) els.storeSearch.addEventListener("keydown", function (e) {
        if (e.key === "Enter") { e.preventDefault(); searchStores(); }
      });
      if (els.storeResults) els.storeResults.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-store-handle]");
        if (btn) loadStoreGraphics(btn.getAttribute("data-mfc-store-handle"));
      });
      if (els.storeGraphics) els.storeGraphics.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-graphic-id]");
        if (btn) selectStoreGraphic(btn.getAttribute("data-mfc-store-handle"), btn.getAttribute("data-mfc-graphic-id"));
      });

      if (els.filters) els.filters.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-filter]");
        if (!btn) return;
        state.activeCategory = btn.getAttribute("data-mfc-filter");
        renderFilters();
        applyFilter();
        fire("bulk_catalog_filter", { item_category: state.activeCategory });
      });

      if (els.search) els.search.addEventListener("input", debounce(function () {
        state.search = (els.search.value || "").trim().toLowerCase();
        applyFilter();
        if (state.search) fire("bulk_catalog_search", { search_term: state.search.slice(0, 80) });
      }, 180));

      if (els.grid) els.grid.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-design]");
        if (btn) openModal(btn.getAttribute("data-mfc-design"));
      });

      if (els.designs) els.designs.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-design-remove]");
        if (btn) removeDesign(btn.getAttribute("data-mfc-design-remove"));
      });

      // Modal interactions
      if (els.modalClose) els.modalClose.addEventListener("click", closeModal);
      if (els.modalBackdrop) els.modalBackdrop.addEventListener("click", closeModal);
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && els.modal && !els.modal.hidden) closeModal();
        else trapFocus(e);
      });

      if (els.swatches) els.swatches.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-swatch]");
        if (!btn || !state.modal) return;
        state.modal.colorIndex = num(btn.getAttribute("data-mfc-swatch"), 0);
        state.modal.qty = {};
        renderModal();
        // The native studio keeps its own colorway; when it is not active,
        // refresh the exact SDK modal preview for the newly selected color.
        if (!state.studio) loadModalPreview();
      });

      if (els.placements) els.placements.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-placement]");
        if (!btn || !state.modal) return;
        state.modal.placementId = btn.getAttribute("data-mfc-placement");
        $all(els.placements, ".mfc__chip").forEach(function (c) {
          c.classList.toggle("is-active", c === btn);
        });
        var placement = (state.modal.product.placements || []).filter(function (p) { return p.id === state.modal.placementId; })[0];
        if (placement) fire("bulk_logo_placement_select", { item_id: state.modal.handle, placement: placement.id });
        updateModalRunning();
      });

      if (els.sizes) els.sizes.addEventListener("input", function (e) {
        var input = e.target.closest("[data-mfc-qty]");
        if (!input || !state.modal) return;
        var size = input.getAttribute("data-mfc-qty");
        var qty = Math.max(0, Math.floor(num(input.value, 0)));
        state.modal.qty[size] = qty;
        updateModalRunning();
      });

      if (els.views) els.views.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-mfc-view]");
        if (!btn || !state.modal) return;
        state.modal.activeView = num(btn.getAttribute("data-mfc-view"), 0);
        renderViews();
      });

      if (els.modalAdd) els.modalAdd.addEventListener("click", addDesign);

      if (els.form) els.form.addEventListener("submit", submitForm);
      [els.name, els.email, els.org].forEach(function (el) {
        if (el) el.addEventListener("focus", fireStartOnce, { once: true });
      });
    }

    function debounce(fn, wait) {
      var timer = null;
      return function () {
        var args = arguments, ctx = this;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    }

    // ---- boot ------------------------------------------------------------
    renderLogoState();
    renderSummary();
    bindEvents();
    loadCatalog();
  }

  function boot() {
    var roots = document.querySelectorAll("[data-mfc-root]");
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute("data-mfc-ready") === "1") return;
      root.setAttribute("data-mfc-ready", "1");
      try { initRoot(root); } catch (err) {
        if (window.console && console.error) console.error("[BulkCustomizer] init failed", err);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
